import { useCallback, useRef } from 'react';
import { App as AntdApp } from 'antd';

import { createPageLayout, updatePageLayout } from '../../services/cardLayouts';
import { normalizeId } from '../../utils/ids';
import { withElementChoicesField } from '../../utils/elementRefusal';
import { adoptForm } from './pageLayoutForm';
import { toBody } from './pageLayoutModel';

/**
 * What the page designer does around a form action (KPR-1860). A form changes on
 * the server at once, so the server has to hold what the person sees first, and
 * its answer has to reach every step of the working copy.
 *
 *   ensureSaved()        before an action: the layout's id, null when a question
 *                        stopped it (the pick is on the page), or a throw with
 *                        what the panel should say
 *   formLanded(layout)   after one: every step takes the form and its paper
 */
export default function usePageFormWrites({
	cardId, layoutId, collectionId, isNew, isDirty, scope, snapshot, current, rebase, resetDirty, elementPicks, loadAttempt, adoptedRef, onAdopted, onClean, onBusy,
}) {
	const { message } = AntdApp.useApp();
	// The person's work as the unsaved-changes guard reads it, as it stands now.
	const workRef = useRef(snapshot);
	workRef.current = snapshot;
	const workNow = useCallback(() => ({ ...workRef.current, body: current() }), [current]);
	// What the work was when an action reached the server, so an edit made while
	// it ran stays an unsaved edit.
	const sentRef = useRef(null);
	// A form action is asking. Otherwise a pick came back here on its own, with no
	// panel waiting to hear how it went.
	const askedRef = useRef(false);

	// Saves the work and stays. A refused pick lands here again with its answer.
	const saveAndStay = useCallback(async (elementChoices) => {
		const asked = askedRef.current;
		const work = workNow();
		const sent = toBody(work.body);
		const named = work.name.trim();
		if (!asked) onBusy(true);
		try {
			let id = layoutId;
			if (isNew) {
				const made = await createPageLayout(cardId, withElementChoicesField({ scope, collectionId: scope === 'card' ? null : collectionId, name: named, body: sent }, elementChoices));
				id = normalizeId(made?._id);
				adoptedRef.current = `${id}:${loadAttempt}`;
				resetDirty(work);
				onAdopted(id);
			} else if (isDirty) {
				await updatePageLayout(cardId, layoutId, withElementChoicesField({ name: named, body: sent }, elementChoices));
				// `resetDirty` also lets the state on screen leave without asking, so an
				// edit made during the save must not reach it: it is still unsaved.
				if (JSON.stringify(workNow()) === JSON.stringify(work)) resetDirty(work);
			}
			sentRef.current = JSON.stringify(work);
			// The file it was saving for has gone: the person chooses it again.
			if (!asked) message.success('Layout saved. Choose the form again.');
			return id;
		} catch (error) {
			if (elementPicks.take(error)) return null;
			if (asked) throw error;
			message.error(error?.response?.data?.message || 'Saving the layout failed.');
			return null;
		} finally {
			if (!asked) onBusy(false);
		}
	}, [workNow, isNew, cardId, scope, collectionId, loadAttempt, resetDirty, adoptedRef, onAdopted, onBusy, isDirty, layoutId, elementPicks, message]);

	// On a new layout this is the only way to a form: Save leaves the editor, and
	// nothing links back to a saved layout yet (KPR-157).
	const ensureSaved = useCallback(async () => {
		if (!workRef.current.name.trim()) throw Object.assign(new Error('no-name'), { sentence: 'Give the layout a name first.' });
		if (!isNew && !isDirty) {
			sentRef.current = JSON.stringify(workNow());
			return layoutId;
		}
		askedRef.current = true;
		try {
			return await elementPicks.attempt(saveAndStay);
		} finally {
			askedRef.current = false;
		}
	}, [isNew, isDirty, layoutId, workNow, elementPicks, saveAndStay]);

	// A form landed, was replaced, was taken off, or its paper changed. Every step
	// takes it, and a working copy nobody touched meanwhile is what is stored.
	const formLanded = useCallback((layout) => {
		const clean = sentRef.current === JSON.stringify(workNow());
		rebase(step => adoptForm(step, layout.body));
		if (clean) onClean();
	}, [workNow, rebase, onClean]);

	return { ensureSaved, formLanded };
}
